"use client"

import { useMemo } from "react"
import { useState, useEffect, useContext, useRef } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  alpha,
  Checkbox,
  Collapse,
  useMediaQuery,
  Drawer,
  Divider,
  Badge,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import CustomPagination from "./CustomPagination"

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility"
import ReplayIcon from "@mui/icons-material/Replay"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from "@mui/icons-material/BarChart"
import TuneIcon from "@mui/icons-material/Tune"
import CloseIcon from "@mui/icons-material/Close"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import BadgeIcon from "@mui/icons-material/Badge"

import { ThemeProvider, createTheme } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import "./TablaVentasComponent.css"
import { GridFooterContainer, GridFooter } from "@mui/x-data-grid"

import dayjs from "dayjs"

import SpinnerLading from "../misc/SpinnerComponent.js"
import ModalViewTablaPresupuesto from "./ModalViewTablaPresupuesto.js"
import GraficasPresupuestosModal from "./graficas/graficas-presupuestos-modal.js"
import Swal from 'sweetalert2';


const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
})

export default function TablaVentas() {
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItemID,
    generarFacturaPDF,
    getUsuario,
    getTransaccionTransformedPresupuesto,
    getEntidadByID,
    getItemById,
    getItemUbicacionFilter,
    generarRemitoPDF,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getTransaccionMontoTotalByTipoTransaccion,
    updateDeleteTransaccion,
    isNavbarCollapsed,
    getFechaHoraActualClient,
    getParametrosGlobales,
    getCanalEntidad,
    getTransaccionTransformedPresupuestosAllData,
    getTransaccionImpuestoItems,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext)

  // Estados principales
  const [openModal, setOpenModal] = useState(false)
  const [transaccionItems, setTransaccionItems] = useState([])
  const [selectedTransaccionId, setSelectedTransaccionId] = useState(null)
  const [selectedTransaccion, setSelectedTransaccion] = useState(null)
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] = useState(null)
  const [rows, setRows] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs();
  const [startDate, setStartDate] = useState(today.subtract(30, 'day'));
  const [endDate, setEndDate] = useState(today);
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [itemsMap, setItemsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [sortedData, setSortedData] = useState([])
  const [dniCliente, setDniCliente] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [filtersActive, setFiltersActive] = useState(false)
  const isFirstRender = useRef(true)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch , setIdTransaccionSearch] = useState(null)
  
  // Estados para canal de entidad
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)
  
  // Estados para gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false)
  const [loadingGraficas, setLoadingGraficas] = useState(false)
  const [datosGraficas, setDatosGraficas] = useState([])
  
  // ===== Responsive flags =====
  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // Estados para el menú lateral (solo desktop)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mostrarFiltroMontos, setMostrarFiltroMontos] = useState(false)
  const [mostrarFiltroCliente, setMostrarFiltroCliente] = useState(false)
  const [mostrarFiltroMediosPago, setMostrarFiltroMediosPago] = useState(false)
  
  // Estados para filtros de atributos de entidad
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({})
  const [atributosEntidadFiltro, setAtributosEntidadFiltro] = useState({})
  

  // Funciones para manipular fechas
  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  // ===== Control del menú lateral =====
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // Función para alternar filtros y cerrar otros
  const toggleFiltro = (filtroNombre) => {
    switch (filtroNombre) {
      case 'montos':
        setMostrarFiltroMontos(!mostrarFiltroMontos)
        setMostrarFiltroCliente(false)
        setMostrarFiltroMediosPago(false)
        break
      case 'cliente':
        setMostrarFiltroCliente(!mostrarFiltroCliente)
        setMostrarFiltroMontos(false)
        setMostrarFiltroMediosPago(false)
        break
      case 'mediosPago':
        setMostrarFiltroMediosPago(!mostrarFiltroMediosPago)
        setMostrarFiltroMontos(false)
        setMostrarFiltroCliente(false)
        break
      default:
        break
    }
  }
  
  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************

  useEffect(() => {

    // console.log("first render after if")
    const today = dayjs()
    setStartDate(today.subtract(30, 'day'));
    setEndDate(today)
    fetchData(startDate, page, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, 100, atributosEntidadFiltro)
    fetchDataInicio()
    setLoading(false)
  }, [])


  useEffect(() => {
    // console.log("efecto startdate")
    fetchData(startDate, page, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, 100, atributosEntidadFiltro)
    setLoading(false)
  }, [startDate])


  useEffect(() => {
    // console.log("effect page")
    // setLoading(true)
    fetchData(startDate, page, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, 100, atributosEntidadFiltro)
    setLoading(false)
  }, [page])

  // Verificar si hay filtros activos
  // useEffect(() => {
  //   // console.log("effect varios")
  //   const hasActiveFilters =
  //     (endDate && startDate && !endDate.isSame(startDate, "day")) ||
  //     dniCliente ||
  //     selectedUbicacion ||
  //     minVentas > 0 ||
  //     maxVentas > 0

  //   setFiltersActive(hasActiveFilters)
  //   setLoading(false)
  // }, [startDate, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas])

  // Funciones para manejar filtros
  const buttonFiltrado = () => {
    fetchData(startDate, 1, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, 100, atributosEntidadFiltro)  // cuando le doy al boton buscar tengo que ir a la pagina 1.
  }

  const fetchDataInicio = async () => {
    try {
      await getUbicacion()
      
      // Cargar canales de entidad si está habilitado
      const canalEntidad = await getParametrosGlobales(["canalEntidad"])
      // console.log("canalEntidad canalEntidad :", canalEntidad.canalEntidad)
      if (canalEntidad.canalEntidad == 1) {
        const datosCanalesEntidades = await getCanalEntidad()
        // console.log("datosCanalesEntidades:", datosCanalesEntidades)
        setCanalesEntidades(datosCanalesEntidades)
      }

      // Cargar atributos de entidad Cliente (ID 1)
      await getEntidadAtributoNoEliminados(1)
    } catch (error) {
      console.error("Error al cargar ubicaciones:", error)
    }
  }

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value)
    if (newMin <= maxVentas) {
      setMinVentas(newMin)
    }
  }

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value)
    if (newMax >= minVentas) {
      setMaxVentas(newMax)
    }
  }

  // Cargar opciones para cada atributo cuando cambian los atributos disponibles
  const cargarOpcionesAtributos = async () => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    const opcionesMap = {}
    for (const attr of entidadAtributoNoEliminados) {
      try {
        const atributoId = attr.idEntidadAtributo || attr.id
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(atributoId, 1) // 1 = Cliente
        opcionesMap[atributoId] = opciones || []
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${attr.idEntidadAtributo || attr.id}:`, error)
        opcionesMap[attr.idEntidadAtributo || attr.id] = []
      }
    }
    setOpcionesPorAtributo(opcionesMap)
  }

  // Inicializar opciones cuando se cargan los atributos disponibles
  useEffect(() => {
    if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
      cargarOpcionesAtributos()
    } else if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadAtributoNoEliminados])

  const fetchData = async (date, page, endDate, dniCliente, selectedUbicacion, valorMin, valorMax, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, limit = 100, atributosEntidad = {}) => {    
    const controller = new AbortController()
    const signal = controller.signal
    try {
      
      setLoading(true)

      let validDate = date ? date : dayjs(); 
      let formattedDate = validDate.format("YYYY-MM-DD");
      // const formattedDate = await date.format("YYYY-MM-DD")
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }
      if (dniCliente != null) {
        idDni = dniCliente.id
      }
      if (selectedUbicacion != null) {
        idSelectedUbicacion = selectedUbicacion.id
      }

const result1 = await getFechaHoraActualClient(formattedDate);
formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate();

const result2 = await getFechaHoraActualClient(formattedDate2);
formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate();

      const data = await getTransaccionTransformedPresupuesto(
        3,
        formattedDate,
        page,
        limit,
        { signal },
        formattedDate2,
        idDni,
        idSelectedUbicacion,
        valorMin,
        valorMax,
        mostrarDeletes,
        idTransaccionSearch,
        '',
        '',
        'todos',
        selectedCanalEntidad?.id || null,
        atributosEntidad
      )
      const transaccionTransformedVentas = data.data

      console.log("transaccionTransformedVentas: ", transaccionTransformedVentas)

      const transaccionTransformedReforged = transaccionTransformedVentas.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }))

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion))

        return {
          id: transaccion.id,
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos: pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          apellido: transaccion.entidad?.apellido || "",
          direccion: transaccion.entidad?.direccion || "",
          localidad: transaccion.entidad?.localidad || "",
          provincia: transaccion.entidad?.provincia || "",
          usuario: transaccion.usuario?.usuario || "",
          negocio: transaccion.negocio?.descripcion || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          idEntidadNumero: transaccion.idEntidad,
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          impuestos: transaccion.impuestos,
          montoDescuento: transaccion.montoDescuento,
          tipoFacturaDescripcion: transaccion.tipoFacturaDescripcion,
          porcentajeDescuento: transaccion.porcentajeDescuento,
        }
      })

      setPage(data.currentPage)
      setTotalPages(data.totalPages)
      setTotalCount(data.total)
      setMontoTotalTransacciones(data.totalMontoVentas)

      setSortedData(transaccionTransformedReforged)
    } catch (error) {
      console.error("Error al cargar los datos:", error)
    } finally {
      // console.log("finally")
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value)
  }

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1)
      setLoading(false)
    }
    
  }, [searchTextCliente])

  useEffect(() => {
    // console.log("effect ti gi")
    const fetchItemsDetails = async () => {
      const itemsDetails = {}

      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem)
          if (foundItem) {
            itemsDetails[transaccionItem.idItem] = foundItem
          }
        }),
      )

      setItemsMap(itemsDetails)
    }

    fetchItemsDetails()
    setLoading(false)
  }, [transaccionItems, getItemById])

  const columns = [
    { field: "id", headerName: "ID", flex: 0.3, minWidth: 70, headerAlign: "center", renderCell: (params) => (
      <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ), },    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      headerAlign: "center",
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1),
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
            display: "flex",
          }}
        />
      ),
    },
    {
      field: "descripcionEntidad",
      headerName: "Cliente",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 150,      
      renderCell: (params) => (
        <Tooltip title={`${params.row.descripcionEntidad} ${params.row.apellido || ""}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7),}} />
            <Typography variant="body2" noWrap fontWeight={'500'}>
              {params.row.descripcionEntidad} {params.row.apellido || ""}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "usuario",
      headerName: "Vendedor",
      headerAlign: "center",
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Acciones",
      headerAlign: "center",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleOpenModal(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("#5b5b5f", 0.1),
                "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleDownloadPDF(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Borrar Transaccion">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => borrarTransaccion(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  const totalVentas = useMemo(() => rows.reduce((acc, row) => acc + (row.montoTotal || 0), 0), [rows])

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
        }}
      >
        Total Ventas: <span style={{ color: "#4CAF50" }}>${totalVentas.toLocaleString("es-AR")}</span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  )

const borrarTransaccion = async (transaccionRow) => {
  
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Vas a eliminar la transacción con ID ${transaccionRow.id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    })
  
    if (result.isConfirmed) {
      try {
        // ejecuta la eliminación
        await updateDeleteTransaccion(transaccionRow.id)
  
        // refresca los datos (ajusta los args a tu fetchData)
        await fetchData(startDate, page, endDate, dniCliente, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, selectedCanalEntidad, 100, atributosEntidadFiltro)

  
        Swal.fire({
          title: "Eliminado",
          text: `La transacción ${transaccionRow.id} ha sido eliminada.`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
      } catch (error) {
        console.error("Error al borrar transacción:", error)
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar la transacción. Intenta de nuevo.",
          icon: "error",
        })
      }
    }
  }

  const handleDownloadPDF = async (transaccionRow) => {
    try {
      setLoading(true)
      console.log("transaccionRow: ", transaccionRow)
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)

      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)

          const precio = transItem?.precio || 0

          return {
            idItem: transItem.idItem,
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            total: (precio * transItem.cantidad).toFixed(2),
          }
        }),
      )

      generarFacturaPDF(transaccionRow, itemsTransaccion, entidadCliente, "presupuesto", transaccionRow.impuestos, "presupuesto")
    } catch (error) {
      console.error("Error al generar PDF:", error)
    } finally {
      // console.log("finally")
      setLoading(false)
    }
  }


  const handleDownloadRemito = async (transaccionRow) => {
    try {
      setLoading(true)
      await generarRemitoPDF(
        transaccionRow?.id,
        null,
        null,
        null,
        null,
        null,
        null,
        { incluirEliminados: false }
      )
    } catch (error) {
      console.error("Error al generar remito:", error)
    } finally {
      // console.log("finally")
      setLoading(false)
    }
  }

  const [calculatedTotal, setCalculatedTotal] = useState(null)
  const [totalMontoDescuento, setTotalMontoDescuento] = useState(null)
  const [totalPorcentajeDescuento, setPorcentajeDescuento] = useState(null)
  const [montoSinImpuesto, setMontoSinImpuesto] = useState(null)

  const handleOpenModal = async (transaccionRow) => {
    try {
      setLoading(true)
      setSelectedTransaccion(transaccionRow)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      setSelectedTransaccionEntidad(entidadCliente)

      const items = await getTransaccionItemID(transaccionRow.id)
      
      // Obtener impuestos de los items de la transacción
      try {
        const impuestosItems = await getTransaccionImpuestoItems(transaccionRow.id)
        console.log("Impuestos de items obtenidos:", impuestosItems)
        
        if (impuestosItems && impuestosItems.length > 0) {
          // Crear mapa de impuestos por item
          const impuestosMap = {}
          impuestosItems.forEach(impuesto => {
            impuestosMap[impuesto.idItem] = {
              idImpuesto: impuesto.idImpuesto,
              porcentaje: impuesto.porcentaje,
              descripcionImpuesto: impuesto.descripcionImpuesto
            }
          })
          
          // Concatenar información de impuestos a los items
          const itemsConImpuestos = items.map(item => ({
            ...item,
            impuestoData: impuestosMap[item.idItem] || null
          }))
          
          setTransaccionItems(itemsConImpuestos)
        } else {
          // Si no hay impuestos, usar items sin modificar
          setTransaccionItems(items)
        }
      } catch (error) {
        console.log("No se encontraron impuestos para esta transacción:", error)
        // Si hay error, usar items sin modificar
        setTransaccionItems(items)
      }

      const { montoTotal, impuestos = [], montoDescuento = 0, porcentajeDescuento = 0 } = transaccionRow

      const totalSinImpuestos = impuestos.reduce((total, impuesto) => total - (impuesto.montoTotal || 0), montoTotal)

      const totalSinImpuestosYDescuento = totalSinImpuestos + montoDescuento
      const totalFinal = totalSinImpuestosYDescuento * (1 - porcentajeDescuento / 100)

      setCalculatedTotal(totalFinal)
      setTotalMontoDescuento(montoDescuento)
      setPorcentajeDescuento(porcentajeDescuento)
      setMontoSinImpuesto(totalSinImpuestos)

      setOpenModal(true)
    } catch (error) {
      console.error("Error al abrir modal:", error)
    } finally {
      // console.log("finally")
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  // Función para abrir modal de gráficas
  const handleOpenGraficasModal = async () => {
    try {
      setLoadingGraficas(true)
      setOpenGraficasModal(true)
      
      const validDate = startDate ? startDate : dayjs()
      let formattedDate = validDate.format("YYYY-MM-DD")
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }
      if (dniCliente != null) idDni = dniCliente.id
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id

      const result1 = await getFechaHoraActualClient(formattedDate)
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate()

      const result2 = await getFechaHoraActualClient(formattedDate2)
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate()
      
      // Llamar a la función sin paginado para obtener todos los datos
      const result = await getTransaccionTransformedPresupuestosAllData(
        3, // Tipo de transacción para presupuestos
        formattedDate,
        formattedDate2,
        idDni || "",
        idSelectedUbicacion || "",
        minVentas || "",
        maxVentas || "",
        mostrarDeletes,
        idTransaccionSearch || "",
        "",
        "",
        "todos",
        selectedCanalEntidad?.id || null,
        atributosEntidadFiltro
      )
      
      setDatosGraficas(result.data)
    } catch (error) {
      console.error("Error al obtener datos para gráficas:", error)
    } finally {
      setLoadingGraficas(false)
    }
  }

  const handleCloseGraficasModal = () => {
    setOpenGraficasModal(false)
    setDatosGraficas([])
  }

  const resetFilters = () => {
    setDniCliente(null)
    setSelectedUbicacion(null)
    setSearchTextCliente("")
    setMinVentas(0)
    setMaxVentas(0)
    setMostrarDeletes(false)
    setIdTransaccionSearch("")
    setSelectedCanalEntidad(null)
    setAtributosEntidadFiltro({})
    setMostrarFiltroMontos(false)
    setMostrarFiltroCliente(false)
    setMostrarFiltroMediosPago(false)

    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
    fetchData(startDate, 1, endDate, null, null, 0, 0, false, null, null, 100, {})
  }

  // Función para renderizar el contenido de los filtros (reutilizable)
  const renderFiltrosContent = () => (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isLaptopDown ? "column" : "row",
        }}
      >
        {/* Filtro de fechas - PRIMERA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: isLaptopDown ? "100%" : 250, width: isLaptopDown ? "100%" : "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <IconButton onClick={decreaseDate} color="primary" size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <DatePicker
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"
              label="Desde"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
            <DatePicker
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"
              label="Hasta"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
            <IconButton onClick={increaseDate} color="primary" size="small">
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </LocalizationProvider>
        </Box>

        {/* Filtro de ID, ubicación y cliente - SEGUNDA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: isLaptopDown ? "100%" : 300, width: isLaptopDown ? "100%" : "auto", flexDirection: isLaptopDown ? "column" : "row" }}>
          <TextField
            label="Buscar por ID"
            type="number"
            size="small"
            value={idTransaccionSearch}
            onChange={(e) => setIdTransaccionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: isLaptopDown ? "100%" : "20%" }}
            fullWidth={isLaptopDown}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={ubicaciones.map((it) => ({ id: it.id, label: it.descripcion }))}
            value={selectedUbicacion}
            onChange={(event, newValue) => setSelectedUbicacion(newValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Ubicación" variant="outlined" />}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={entidadesClientesTresLetras.map((it) => ({
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
              ...it,
            }))}
            value={dniCliente}
            onChange={(event, newValue) => {
              setDniCliente(newValue || null)
            }}
            onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Cliente - DNI / CUIT / CUIL" variant="outlined" />}
          />
        </Box>

        {/* Botones de acción (solo desktop) */}
        {!isLaptopDown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenGraficasModal}
              disabled={loading}
              startIcon={<BarChartIcon />}
              sx={{
                borderColor: "rgb(244, 177, 14)",
                color: "rgb(244, 177, 14)",
                textTransform: "none",
                padding: "8px 16px",
                "&:hover": {
                  borderColor: alpha("rgb(244, 177, 14)", 0.8),
                  backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                },
              }}
            >
              Gráficas
            </Button>

            <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
              <IconButton
                onClick={resetFilters}
                color="primary"
                size="small"
                sx={{
                  bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={sidebarOpen ? "Cerrar Más Filtros" : "Mostrar Más Filtros"}>
              <Badge 
                badgeContent={
                  (minVentas > 0 || maxVentas > 0 ? 1 : 0) +
                  (selectedCanalEntidad ? 1 : 0) +
                  (Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0)
                } 
                color="secondary"
              >
              <IconButton
                  onClick={toggleSidebar}
                color="primary"
                size="small"
                sx={{
                    bgcolor: sidebarOpen ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <TuneIcon />
              </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Boxes de filtros activos (debajo del menú principal) */}
      {!isLaptopDown && (minVentas > 0 || maxVentas > 0 || dniCliente || selectedUbicacion || selectedCanalEntidad || Object.values(atributosEntidadFiltro).some(v => v)) && (
        <Box
          sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, color: theme.palette.text.secondary }}>
            Filtros activos:
          </Typography>
          
          {/* Chip de Montos */}
          {(minVentas > 0 || maxVentas > 0) && (
            <Chip
              label={`Montos: ${minVentas > 0 ? `$${minVentas.toLocaleString("es-AR")}` : "Sin mínimo"} - ${maxVentas > 0 ? `$${maxVentas.toLocaleString("es-AR")}` : "Sin máximo"}`}
              size="small"
              onDelete={() => {
                setMinVentas(0)
                setMaxVentas(0)
                setMostrarFiltroMontos(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Cliente */}
          {dniCliente && (
            <Chip
              label={`Cliente: ${dniCliente.descripcion || dniCliente.label || dniCliente.dniCuitCuil || ""}`}
              size="small"
              onDelete={() => {
                setDniCliente(null)
                setSearchTextCliente("")
                setMostrarFiltroCliente(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Ubicación */}
          {selectedUbicacion && (
            <Chip
              label={`Ubicación: ${selectedUbicacion.label || selectedUbicacion.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedUbicacion(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Canal */}
          {selectedCanalEntidad && (
            <Chip
              label={`Canal: ${selectedCanalEntidad.label || selectedCanalEntidad.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedCanalEntidad(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chips de Atributos de Entidad */}
          {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && entidadAtributoNoEliminados.map((atributo, index) => {
            const atributoNumero = index + 1
            const valorAtributo = atributosEntidadFiltro[atributoNumero]
            if (!valorAtributo) return null
            
            const atributoId = atributo.idEntidadAtributo || atributo.id
            const opciones = opcionesPorAtributo[atributoId] || []
            const opcionSeleccionada = opciones.find(o => o.id === valorAtributo)
            
            return (
              <Chip
                key={atributoId}
                label={`${atributo.descripcion}: ${opcionSeleccionada?.descripcion || "N/A"}`}
                size="small"
                onDelete={() => {
                  setAtributosEntidadFiltro(prev => {
                    const newAtributos = { ...prev }
                    delete newAtributos[atributoNumero]
                    return newAtributos
                  })
                  buttonFiltrado()
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Boxes de filtros expandibles (debajo del menú principal) */}
      {!isLaptopDown && (mostrarFiltroMontos || mostrarFiltroCliente || mostrarFiltroMediosPago) && (
        <Box
          sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          {/* Subbox de Montos */}
          {mostrarFiltroMontos && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <AttachMoneyIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Rango de Montos
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroMontos(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  label="Mínimo"
                  type="number"
                  size="small"
                  value={minVentas}
                  onChange={handleMinChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Máximo"
                  type="number"
                  size="small"
                  value={maxVentas}
                  onChange={handleMaxChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar
                </Button>
                {(minVentas > 0 || maxVentas > 0) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setMinVentas(0)
                      setMaxVentas(0)
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Subbox de Atributos de Entidad */}
          {mostrarFiltroCliente && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Filtrar por Atributos de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroCliente(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Filtros por Atributos de Entidad */}
              <Box sx={{ mb: 2 }}>
                {entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0 && (
                  <Chip 
                    label={`${entidadAtributoNoEliminados.length} atributos disponibles`} 
                    size="small" 
                    sx={{ mb: 2, color: "#5b5b5f" }} 
                  />
                )}
                
                {(() => {
                  const tieneAtributos = entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0
                  
                  if (!tieneAtributos) {
                    return (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}>
                        No hay atributos de entidad configurados. Ve a Administración → Atributos de Entidad para configurarlos.
                      </Typography>
                    )
                  }
                  
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {entidadAtributoNoEliminados.map((atributo, index) => {
                        const atributoNumero = index + 1
                        const atributoId = atributo.idEntidadAtributo || atributo.id
                        const opciones = opcionesPorAtributo[atributoId] || []
                        
                        return (
                          <Autocomplete
                            key={atributoId}
                            size="small"
                            disablePortal
                            options={opciones}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={opciones.find(o => o.id === atributosEntidadFiltro[atributoNumero]) || null}
                            onChange={(event, newValue) => {
                              setAtributosEntidadFiltro(prev => {
                                const newAtributos = { ...prev }
                                if (newValue) {
                                  newAtributos[atributoNumero] = newValue.id
                                } else {
                                  delete newAtributos[atributoNumero]
                                }
                                return newAtributos
                              })
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={atributo.descripcion}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: atributosEntidadFiltro[atributoNumero]
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : "white",
                                  },
                                }}
                              />
                            )}
                          />
                        )
                      })}
                    </Box>
                  )
                })()}
              </Box>

              {/* Botones de Acción */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar Filtros
                </Button>
                {Object.values(atributosEntidadFiltro).filter(v => v).length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setAtributosEntidadFiltro({})
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar Atributos
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Subbox de Medios de Pago y Canal */}
          {mostrarFiltroMediosPago && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PointOfSaleIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Canal de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroMediosPago(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                {/* Canal */}
                {canalesEntidades && canalesEntidades.length > 0 && (
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={canalesEntidades}
                    getOptionLabel={(option) => option.descripcion || ""}
                    value={selectedCanalEntidad}
                    onChange={(event, newValue) => {
                      setSelectedCanalEntidad(newValue)
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                    renderInput={(params) => <TextField {...params} label="Canal de Entidad" variant="outlined" size="small" />}
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar
                </Button>
                {selectedCanalEntidad && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedCanalEntidad(null)
                      buttonFiltrado()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Paper>
          )}

        </Box>
      )}
    </>
  )

  // Renderiza el spinner mientras está cargando - Usando la lógica original
  if (loading) {
    return <SpinnerLading />
  }

// ************************************************************************************************************************************************************
// ************************************************************************************************************************************************************
// ************************************************************************************************************************************************************
// ************************************************************************************************************************************************************
// ************************************************************************************************************************************************************
// ************************************************************************************************************************************************************
// html:

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Contenedor de filtros para Desktop (siempre visible) */}
        {!isLaptopDown && (
          <Collapse in={true} timeout={250}>
            <Paper
              className="diseñoFiltroTransaccionesVenta"
              elevation={3}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                maxHeight: "40vh",
                overflow: "auto",
                flexShrink: 0,
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.6),
                  },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: "4px",
                },
              }}
            >
              {renderFiltrosContent()}
            </Paper>
          </Collapse>
        )}

        {/* Menú lateral (solo desktop) */}
        {!isLaptopDown && (
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="persistent"
            sx={{
              "& .MuiDrawer-paper": {
                width: 80,
                top: 0,
                height: "100vh",
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
                zIndex: 1200,
                overflowX: "hidden",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
              {/* Botón de cerrar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <IconButton size="small" onClick={toggleSidebar}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* 1. Atributos de Entidad */}
                <Tooltip title="Filtros por Atributos de Entidad" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('cliente')}
                    sx={{
                      color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroCliente ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0} 
                      color="secondary"
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 2. Canal */}
                {canalesEntidades && canalesEntidades.length > 0 && (
                  <Tooltip title="Filtros de Canal" placement="left">
                    <IconButton
                      onClick={() => toggleFiltro('mediosPago')}
                      sx={{
                        color: mostrarFiltroMediosPago || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        "& svg": {
                          color: mostrarFiltroMediosPago || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        },
                        bgcolor: mostrarFiltroMediosPago ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                        "&:hover": {
                          bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                        },
                      }}
                    >
                      <Badge 
                        badgeContent={selectedCanalEntidad ? 1 : 0} 
                        color="secondary"
                      >
                        <PointOfSaleIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                {/* 3. Montos */}
                <Tooltip title="Filtros de Montos" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('montos')}
                    sx={{
                      color: mostrarFiltroMontos || minVentas > 0 || maxVentas > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroMontos || minVentas > 0 || maxVentas > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroMontos ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge badgeContent={minVentas > 0 || maxVentas > 0 ? 1 : 0} color="secondary">
                      <AttachMoneyIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 4. Mostrar Eliminados */}
                <Tooltip title={mostrarDeletes ? "Ocultar eliminados" : "Mostrar eliminados"} placement="left">
                  <IconButton
                    onClick={() => {
                      const nuevoEstado = !mostrarDeletes
                      setMostrarDeletes(nuevoEstado)
                      // Llamar a fetchData directamente con el nuevo estado para evitar problemas de sincronización
                      fetchData(
                        startDate,
                        page,
                        endDate,
                        dniCliente,
                        selectedUbicacion,
                        minVentas,
                        maxVentas,
                        nuevoEstado,
                        idTransaccionSearch,
                        selectedCanalEntidad,
                        100,
                        atributosEntidadFiltro
                      )
                    }}
                    sx={{
                      color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarDeletes ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>

                <Divider sx={{ my: 1 }} />

              </Box>
            </Box>
          </Drawer>
        )}

        {/* Contenedor de la tabla (scrollable siempre dentro de la tarjeta) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={sortedData}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            getRowHeight={() => 35}
            headerHeight={35}         
            paginationMode="server"
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": { //MANEJA CSS HEADER
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": { //MANEJA CSS HEADER
                height: "45px !important",
                minHeight:       "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": { //MANEJA CSS HEADER
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
            components={{
              Pagination: () => null,
            }}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaVentas"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        {/* Modal */}
        <ModalViewTablaPresupuesto
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          selectedTransaccion={selectedTransaccion}
          selectedTransaccionEntidad={selectedTransaccionEntidad}
          transaccionItems={transaccionItems}
        />

        {/* Modal de Gráficas */}
        <GraficasPresupuestosModal
          open={openGraficasModal}
          handleClose={handleCloseGraficasModal}
          data={datosGraficas}
          loading={loadingGraficas}
        />
      </ThemeProvider>
    </div>
  )
}

